/**
 * Shared PTY/xterm client for plugin renderers.
 *
 * This component owns the common terminal-sidecar client plumbing after a PTY
 * session has already been opened by a plugin or action:
 * - xterm.js + fit addon setup
 * - theme sync with Magic MD editor theme variables
 * - keyboard input -> terminal/pty-write
 * - terminal resize -> terminal/pty-resize
 * - event/stream.chunk -> xterm output
 * - terminal/pty-list liveness check
 *
 * It deliberately does not open sessions. Plugins differ in how they create
 * sessions (plain shell, docker exec, docker run, tmux attach, OrbStack, etc.).
 */

import type { FitAddon as FitAddonInstance } from "@xterm/addon-fit";
import type { ITerminalOptions, Terminal as XTermInstance } from "@xterm/xterm";
import {
  forwardRef,
  type CSSProperties,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import type { RendererProps } from "../types/index";
import {
  ResizableViewport,
  type ResizableViewportHandlePosition,
} from "./ResizableViewport";
import { usePluginSdkT } from "../i18n";
import { getPluginTerminalTheme } from "./theme-helpers";

const PTY_TERMINAL_CSS = `
.magic-md-pty-terminal .xterm {
    cursor: text;
    height: 100%;
    position: relative;
    user-select: none;
}
.magic-md-pty-terminal .xterm.focus,
.magic-md-pty-terminal .xterm:focus {
    outline: none;
}
.magic-md-pty-terminal .xterm .xterm-helpers {
    position: absolute;
    top: 0;
    z-index: 5;
}
.magic-md-pty-terminal .xterm .xterm-helper-textarea {
    border: 0;
    height: 0;
    left: -9999em;
    margin: 0;
    opacity: 0;
    overflow: hidden;
    padding: 0;
    position: absolute;
    resize: none;
    top: 0;
    white-space: nowrap;
    width: 0;
    z-index: -5;
}
.magic-md-pty-terminal .xterm .composition-view {
    background: #000;
    color: #fff;
    display: none;
    position: absolute;
    white-space: nowrap;
    z-index: 1;
}
.magic-md-pty-terminal .xterm .composition-view.active {
    display: block;
}
.magic-md-pty-terminal .xterm .xterm-viewport {
    background-color: transparent;
    bottom: 0;
    cursor: default;
    left: 0;
    overflow-y: scroll;
    position: absolute;
    right: 0;
    top: 0;
}
.magic-md-pty-terminal .xterm .xterm-screen {
    position: relative;
}
.magic-md-pty-terminal .xterm .xterm-screen canvas {
    left: 0;
    position: absolute;
    top: 0;
}
.magic-md-pty-terminal .xterm .xterm-scroll-area {
    visibility: hidden;
}
.magic-md-pty-terminal .xterm-char-measure-element {
    display: inline-block;
    left: -9999em;
    line-height: normal;
    position: absolute;
    top: 0;
    visibility: hidden;
}
.magic-md-pty-terminal .xterm.enable-mouse-events {
    cursor: default;
}
.magic-md-pty-terminal .xterm.xterm-cursor-pointer,
.magic-md-pty-terminal .xterm .xterm-cursor-pointer {
    cursor: pointer;
}
.magic-md-pty-terminal .xterm.column-select.focus {
    cursor: crosshair;
}
.magic-md-pty-terminal .xterm .xterm-accessibility:not(.debug),
.magic-md-pty-terminal .xterm .xterm-message {
    bottom: 0;
    color: transparent;
    left: 0;
    pointer-events: none;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 10;
}
.magic-md-pty-terminal .xterm .xterm-accessibility-tree:not(.debug) *::selection {
    color: transparent;
}
.magic-md-pty-terminal .xterm .xterm-accessibility-tree {
    user-select: text;
    white-space: pre;
}
.magic-md-pty-terminal .xterm .live-region {
    height: 1px;
    left: -9999px;
    overflow: hidden;
    position: absolute;
    width: 1px;
}
.magic-md-pty-terminal .xterm-dim {
    opacity: 1 !important;
}
`;

export type PtyTerminalStatus = "connecting" | "active" | "ended" | "error";

export type PtyTerminalCallSidecar = NonNullable<RendererProps["callSidecar"]>;
export type PtyTerminalNotificationSubscriber = NonNullable<
  RendererProps["onEngineNotification"]
>;

export interface PtyTerminalStreamChunkEvent {
  streamId: string;
  topic?: string;
  data?: string;
  seq?: number;
  eof?: boolean;
}

export interface PtyTerminalHandle {
  fit: () => void;
  focus: () => void;
  refresh: () => void;
  write: (data: string) => void;
}

export interface PtyTerminalProps {
  sessionId: string;
  callSidecar?: PtyTerminalCallSidecar | null;
  onEngineNotification?: PtyTerminalNotificationSubscriber | null;
  onStatusChange?: (status: PtyTerminalStatus) => void;
  onEof?: () => void;

  /** Send a small input after mount, useful for forcing a fresh prompt. */
  initialInput?: string;
  initialInputDelayMs?: number;
  /** Focus the xterm after setup. Default false to avoid stealing editor focus. */
  autoFocus?: boolean;
  /** Verify session existence via terminal/pty-list. Default true. */
  checkLiveness?: boolean;

  fontSize?: number;
  fontFamily?: string;
  scrollback?: number;

  /** Use SDK ResizableViewport. Disable for fully custom outer layout. */
  resizable?: boolean;
  /** Fill the parent height, typically when RendererProps.layout.fillHeight is true. */
  fillAvailable?: boolean;
  defaultHeight?: number;
  minHeight?: number;
  maxHeight?: number;
  handlePosition?: ResizableViewportHandlePosition;
  storageKey?: string;
  className?: string;
  contentClassName?: string;
  contentStyle?: CSSProperties;
  terminalClassName?: string;
  terminalStyle?: CSSProperties;

  livenessFailedMessage?: string;
  missingSessionMessage?: string;
  endedMessage?: string;
}

function useLatestRef<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}

export const PtyTerminal = forwardRef<PtyTerminalHandle, PtyTerminalProps>(
  function PtyTerminal(
    {
      sessionId,
      callSidecar,
      onEngineNotification,
      onStatusChange,
      onEof,
      initialInput,
      initialInputDelayMs = 150,
      autoFocus = false,
      checkLiveness = true,
      fontSize = 13,
      fontFamily = "'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, monospace",
      scrollback,
      resizable = true,
      fillAvailable = false,
      defaultHeight = 320,
      minHeight = 140,
      maxHeight,
      handlePosition,
      storageKey,
      className,
      contentClassName,
      contentStyle,
      terminalClassName,
      terminalStyle,
      livenessFailedMessage,
      missingSessionMessage,
      endedMessage,
    },
    ref,
  ) {
    const t = usePluginSdkT();
    const resolvedLivenessFailedMessage = livenessFailedMessage ?? t("pty.livenessFailedMessage");
    const resolvedMissingSessionMessage = missingSessionMessage ?? t("pty.missingSessionMessage");
    const resolvedEndedMessage = endedMessage ?? t("pty.endedMessage");
    const hostRef = useRef<HTMLDivElement>(null);
    const termRef = useRef<XTermInstance | null>(null);
    const fitRef = useRef<FitAddonInstance | null>(null);
    const onEngineNotificationRef = useLatestRef(onEngineNotification);
    const onStatusChangeRef = useLatestRef(onStatusChange);
    const onEofRef = useLatestRef(onEof);

    const emitStatus = useCallback(
      (status: PtyTerminalStatus) => {
        onStatusChangeRef.current?.(status);
      },
      [onStatusChangeRef],
    );

    const fitTerminal = useCallback(() => {
      const host = hostRef.current;
      if (!host || host.clientWidth <= 0 || host.clientHeight <= 0) return;
      try {
        fitRef.current?.fit();
        const term = termRef.current;
        if (term && term.rows > 0) term.refresh(0, term.rows - 1);
      } catch {
        // xterm fit can throw while the host is hidden or detached.
      }
    }, []);

    const refreshTerminal = useCallback(() => {
      fitTerminal();
    }, [fitTerminal]);

    useImperativeHandle(
      ref,
      () => ({
        fit: fitTerminal,
        focus: () => termRef.current?.focus(),
        refresh: refreshTerminal,
        write: (data: string) => termRef.current?.write(data),
      }),
      [fitTerminal, refreshTerminal],
    );

    useEffect(() => {
      if (!hostRef.current || !callSidecar) return;

      emitStatus("connecting");

      const applyTerminalTheme = (term: XTermInstance) => {
        term.options.theme = getPluginTerminalTheme();
      };

      let alive = true;
      let inputDisposable: { dispose: () => void } | null = null;
      let resizeDisposable: { dispose: () => void } | null = null;
      let unsubStream: (() => void) | undefined;
      let ro: ResizeObserver | null = null;
      let themeObserver: MutationObserver | null = null;
      let initialInputTimer: number | null = null;
      let windowResizeAttached = false;
      const onWindowResize = () => fitTerminal();
      const scheduleFit = () => {
        requestAnimationFrame(() => {
          fitTerminal();
          requestAnimationFrame(() => fitTerminal());
        });
      };

      void (async () => {
        try {
          const [{ FitAddon }, { Terminal: XTerm }] = await Promise.all([
            import("@xterm/addon-fit"),
            import("@xterm/xterm"),
          ]);

          if (!alive || !hostRef.current) return;

          const terminalOptions: ITerminalOptions = {
            allowProposedApi: true,
            cursorBlink: true,
            fontFamily,
            fontSize,
            theme: getPluginTerminalTheme(),
          };
          if (scrollback !== undefined) terminalOptions.scrollback = scrollback;

          const term = new XTerm(terminalOptions);
          const fit = new FitAddon();

          term.loadAddon(fit);
          term.open(hostRef.current);
          applyTerminalTheme(term);

          termRef.current = term;
          fitRef.current = fit;

          scheduleFit();
          void document.fonts?.ready.then(() => {
            if (alive) scheduleFit();
          });

          inputDisposable = term.onData((data: string) => {
            callSidecar("terminal", "terminal/pty-write", {
              sessionId,
              data,
            }).catch(() => {});
          });

          resizeDisposable = term.onResize(
            ({ cols, rows }: { cols: number; rows: number }) => {
              callSidecar("terminal", "terminal/pty-resize", {
                sessionId,
                cols,
                rows,
              }).catch(() => {});
            },
          );

          callSidecar("terminal", "terminal/pty-resize", {
            sessionId,
            cols: term.cols,
            rows: term.rows,
          }).catch(() => {});

          emitStatus("active");

          if (checkLiveness) {
            callSidecar("terminal", "terminal/pty-list", {})
              .then((response) => {
                if (!alive) return;
                if (!response.success) {
                  term.write(`\r\n\x1b[31m${resolvedLivenessFailedMessage}\x1b[0m\r\n`);
                  return;
                }

                const result =
                  response.result && typeof response.result === "object"
                    ? (response.result as {
                        sessions?: Array<{ sessionId?: string }>;
                      })
                    : {};
                const sessions = Array.isArray(result.sessions)
                  ? result.sessions
                  : [];
                const exists = sessions.some(
                  (session) => session.sessionId === sessionId,
                );

                if (exists) {
                  emitStatus("active");
                } else {
                  emitStatus("ended");
                  term.write(`\r\n\x1b[2m${resolvedMissingSessionMessage}\x1b[0m\r\n`);
                }
              })
              .catch(() => {
                if (alive) emitStatus("active");
              });
          } else {
            emitStatus("active");
          }

          unsubStream =
            onEngineNotificationRef.current?.<PtyTerminalStreamChunkEvent>(
              "event/stream.chunk",
              (ev) => {
                if (ev.streamId !== sessionId) return;
                if (ev.data) term.write(ev.data);
                if (ev.eof) {
                  emitStatus("ended");
                  term.write(`\r\n\x1b[2m${resolvedEndedMessage}\x1b[0m\r\n`);
                  onEofRef.current?.();
                }
              },
            );

          window.addEventListener("resize", onWindowResize);
          windowResizeAttached = true;

          ro = new ResizeObserver(() => fitTerminal());
          ro.observe(hostRef.current);

          themeObserver =
            typeof MutationObserver === "undefined"
              ? null
              : new MutationObserver(() => {
                  applyTerminalTheme(term);
                });
          themeObserver?.observe(document.documentElement, {
            attributeFilter: ["data-color-scheme", "data-theme", "style"],
            attributes: true,
          });

          if (initialInput) {
            initialInputTimer = window.setTimeout(() => {
              callSidecar("terminal", "terminal/pty-write", {
                sessionId,
                data: initialInput,
              }).catch(() => {});
            }, initialInputDelayMs);
          }

          if (autoFocus) term.focus();
        } catch (error) {
          if (!alive) return;
          emitStatus("error");
          const message =
            error instanceof Error ? error.message : String(error);
          // Keep the failure visible in developer tooling; there is no xterm
          // instance to write into when module loading itself fails.
          console.error("[PtyTerminal] failed to initialize xterm", message);
        }
      })();

      return () => {
        alive = false;
        if (initialInputTimer !== null) window.clearTimeout(initialInputTimer);
        if (windowResizeAttached)
          window.removeEventListener("resize", onWindowResize);
        ro?.disconnect();
        themeObserver?.disconnect();
        inputDisposable?.dispose();
        resizeDisposable?.dispose();
        unsubStream?.();
        termRef.current?.dispose();
        termRef.current = null;
        fitRef.current = null;
      };
    }, [
      autoFocus,
      callSidecar,
      checkLiveness,
      emitStatus,
      resolvedEndedMessage,
      fitTerminal,
      fontFamily,
      fontSize,
      initialInput,
      initialInputDelayMs,
      resolvedLivenessFailedMessage,
      resolvedMissingSessionMessage,
      onEofRef,
      onEngineNotificationRef,
      scrollback,
      sessionId,
    ]);

    const terminalNode = (
      <>
        <style data-magic-md-pty-terminal>{PTY_TERMINAL_CSS}</style>
        <div
          ref={hostRef}
          className={`magic-md-pty-terminal ${terminalClassName ?? "h-full w-full"}`}
          style={terminalStyle}
        />
      </>
    );

    if (!callSidecar) {
      return (
        <div
          className={
            className ??
            "rounded border border-border bg-background p-4 text-center"
          }
        >
          <p className="text-sm text-muted-foreground">
            {t("pty.requiresTerminalSidecar")}
          </p>
        </div>
      );
    }

    if (!resizable) {
      return (
        <div
          className={className}
          style={fillAvailable
            ? {
                ...contentStyle,
                background: "var(--editor-console-surface)",
                height: "100%",
                minHeight: 0,
              }
            : {
                background: "var(--editor-console-surface)",
                height: defaultHeight,
                ...contentStyle,
              }}
        >
          {terminalNode}
        </div>
      );
    }

    return (
      <ResizableViewport
        fillAvailable={fillAvailable}
        className={className}
        contentClassName={contentClassName}
        contentStyle={{
          background: "var(--editor-console-surface)",
          padding: 4,
          ...contentStyle,
        }}
        defaultHeight={defaultHeight}
        max={maxHeight}
        min={minHeight}
        handlePosition={handlePosition}
        storageKey={storageKey}
      >
        {terminalNode}
      </ResizableViewport>
    );
  },
);
